import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {useFieldArray} from 'react-hook-form';
import {PlusCircle} from 'lucide-react';
import {FormLabel} from "../sharedStyles.tsx";
import {DataGridRow} from './DataGridRow';
import {DataGridComponent, FormComponentProps} from "../../DynamicFormTypes.ts";
import {ValidationFactory} from "../../ValidationFactory.ts";
import {useFormInfo, useFormMethods, useFormStatic} from "../../DyanmicFormContext.tsx";
import {DataGridHeader} from "./DataGridHeader.tsx";
import {DataGridColumn} from "./DataGridColumn.tsx";
import {getDefaultRowForDataGrid} from "../../FormRenderer.tsx";
import {useFieldBlurHandler} from "../../hooks/OnFieldBlurHanlder.tsx";

/**
 * DataGrid component for rendering dynamic form rows with configurable fields
 * Uses react-hook-form's useFieldArray for row management
 * Supports both row and column layouts
 */
const DataGridRenderer: React.FC<FormComponentProps<DataGridComponent>> = ({ component }) => {
    const { formMethods } = useFormMethods()
    const { metaData } = useFormStatic();
    const formInfoContext = useFormInfo();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const parentContainerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = React.useState<number>(0);

    const {
        fieldKey,
        components = [],
        label,
        description,
        useColumnLayout = false,
    } = component;


    const minLength = ValidationFactory.getMinValue(component, 0);
    const maxLength = ValidationFactory.getMaxValue(component, undefined);
    const itemName = component.itemName ? component.itemName : "Item";

    // Use useFieldArray to manage dynamic rows
    const { fields, append, remove } = useFieldArray({
        control: formMethods.control,
        name: fieldKey,
        shouldUnregister: false,
        rules: { minLength: minLength, required: !!(minLength && minLength > 0) },
    });

    // Measure container width on mount and resize
    useEffect(() => {
        const measureWidth = () => {
            if (parentContainerRef.current) {
                const width = parentContainerRef.current.offsetWidth;
                // Account for border (2px total)
                setContainerWidth(width - 2);
            }
        };

        measureWidth();
        window.addEventListener('resize', measureWidth);

        // Also measure when fields change (in case it affects layout)
        const timeout = setTimeout(measureWidth, 100);

        return () => {
            window.removeEventListener('resize', measureWidth);
            clearTimeout(timeout);
        };
    }, [fields.length]);

    const { handleFieldBlur } = useFieldBlurHandler({
        component
    });

    // Validation function for minimum row requirements
    const validationFunction = useCallback((value: any) => {
        return (
            (value && value.length >= (minLength || 0)) ||
            `At least ${minLength} items are required`
        );
    }, [minLength]);

    // Register validation rules
    useEffect(() => {
        if (minLength) {
            formMethods.register(fieldKey, {
                validate: validationFunction
            });
        }
    }, [fieldKey, minLength, validationFunction, formMethods]);

    // Add a new row
    const handleAddRow = useCallback(() => {
        append(getDefaultRowForDataGrid(component, metaData));
        setTimeout(handleFieldBlur, 0);
        // Scroll to the right when adding a new column in column layout
        if (useColumnLayout && scrollContainerRef.current) {
            setTimeout(() => {
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
                }
            }, 100);
        }
    }, [append, handleFieldBlur, fields.length, fieldKey, useColumnLayout]);

    // Remove a row (respecting minLength)
    const handleRemoveRow = useCallback((rowIndex: number) => {
        if (minLength === undefined || fields.length > minLength) {
            remove(rowIndex);
            formInfoContext.clearDataGridRowInfosAndMoveSubsequentRows(rowIndex, component.fieldKey);
            setTimeout(handleFieldBlur, 0);
        }
    }, [minLength, fields.length, remove, handleFieldBlur, fieldKey]);

    const fieldState = formMethods.getFieldState(fieldKey);
    const memoizedComponents = useMemo(() => components, [components]);
    const memoizedHeaderConfig = useMemo(() => component.headerConfig, [component.headerConfig]);

    const renderDataGridRows = () => {
        return (
            // Row Layout: Each thing is a row.
            <div className="divide-y divide-gray-100">
                <DataGridHeader
                    key={`${fieldKey}-header-row`}
                    config={memoizedHeaderConfig}
                    useColumnLayout={useColumnLayout}/>
                {fields.map((field, index) => (
                    <DataGridRow
                        key={field.id}
                        field={field}
                        index={index}
                        components={memoizedComponents}
                        fieldKey={fieldKey}
                        minLength={minLength}
                        totalCount={fields.length}
                        handleRemoveRow={handleRemoveRow}
                        itemName={itemName}
                    />
                ))}
            </div>
        )
    }



    const renderDataGridColumns = () => {
        const COLUMN_WIDTH = 280; // Single source of truth for column width
        const headerCount = memoizedHeaderConfig ? 1 : 0;
        const totalColumns = fields.length + headerCount;
        const tableWidth = totalColumns * COLUMN_WIDTH;

        // Use container width if available, otherwise use full width
        const maxWidth = containerWidth > 0 ? containerWidth : '100%';

        return (
            // Column Layout: Each field is a column, components stack vertically
            <div
                ref={scrollContainerRef}
                className="overflow-x-visible"
                style={{
                    maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
                    width: '100%'
                }}
            >
                <table
                    className="table-fixed"
                    style={{
                        width: `${tableWidth}px`,
                        minWidth: '100%'
                    }}
                >
                    <tbody>
                    <tr>
                        {/* Optional: Render header as first column */}
                        {memoizedHeaderConfig && (
                            <td
                                className="align-top p-0"
                                style={{ width: `${COLUMN_WIDTH}px` }}
                            >
                                <DataGridHeader
                                    key={`${fieldKey}-header-column`}
                                    config={memoizedHeaderConfig}
                                    useColumnLayout={useColumnLayout}
                                />
                            </td>
                        )}

                        {/* Render each field as a column */}
                        {fields.map((field, index) => (
                            <td
                                key={field.id}
                                className="align-top p-0"
                                style={{ width: `${COLUMN_WIDTH}px` }}
                            >
                                <DataGridColumn
                                    field={field}
                                    index={index}
                                    components={memoizedComponents}
                                    fieldKey={fieldKey}
                                    minLength={minLength}
                                    totalCount={fields.length}
                                    handleRemoveRow={handleRemoveRow}
                                    itemName={itemName}
                                />
                            </td>
                        ))}
                    </tr>
                    </tbody>
                </table>
            </div>
        )
    }

    return (
        <div className="mb-8 w-full">
            {/* Header with DataGrid label and description */}
            {label && (
                <div className="mb-3">
                    <FormLabel component={component} />
                    {description && (
                        <p className="mt-1 text-sm text-gray-500">{description}</p>
                    )}
                </div>
            )}

            {/* Data Grid Container */}
            <div
                ref={parentContainerRef}
                className={`border ${fieldState.error ? 'border-red-300' : 'border-gray-200'} overflow-visible rounded-lg w-full shadow-sm`}
            >
                {/* Data Items */}
                {useColumnLayout ? (renderDataGridColumns()) : (renderDataGridRows())}
                {/* Empty state */}
                {fields.length === 0 && (
                    <div className="py-8 text-center text-gray-500">
                        <p>No {itemName.toLowerCase()}s available. Add a new {itemName.toLowerCase()} to get started.</p>
                    </div>
                )}

                {/* Footer with Add Button */}
                <div className="p-3 bg-gray-50 border-t border-gray-200">
                    {(!maxLength || fields.length < maxLength) && (
                        <button
                            type="button"
                            onClick={handleAddRow}
                            className="inline-flex items-center px-4 py-2 border border-transparent
                            text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600
                            hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2
                            focus:ring-indigo-500 transition-colors"
                        >
                            <PlusCircle className="h-4 w-4 mr-2" />
                            Add {itemName}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export const DataGrid = React.memo(DataGridRenderer);